import React from 'react';
import { Box, Typography, Chip, IconButton, Badge } from '@mui/material';
import {
  Refresh as RefreshIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
} from '@mui/icons-material';
import { SvgIconComponent } from '@mui/icons-material';

export interface ObservatoryHeaderProps {
  icon: React.ReactElement<SvgIconComponent>;
  title: string;
  subtitle?: string;
  connected?: boolean;
  showConnectionStatus?: boolean;
  eventCount?: number;
  lastRefresh?: Date;
  refreshing?: boolean;
  onRefresh?: () => void;
  actions?: React.ReactNode;
}

export const ObservatoryHeader: React.FC<ObservatoryHeaderProps> = ({
  icon,
  title,
  subtitle,
  connected = true,
  showConnectionStatus = false,
  eventCount = 0,
  lastRefresh,
  refreshing = false,
  onRefresh,
  actions,
}) => {
  const getTimeSinceRefresh = () => {
    if (!lastRefresh) return null;
    const seconds = Math.round((Date.now() - lastRefresh.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {React.cloneElement(icon, { sx: { fontSize: 28, color: 'primary.main' } })}
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {showConnectionStatus && (
          <Chip
            icon={connected ? <WifiIcon /> : <WifiOffIcon />}
            label={connected ? 'Connected' : 'Disconnected'}
            color={connected ? 'success' : 'error'}
            variant="outlined"
            size="small"
          />
        )}
        {eventCount > 0 && (
          <Badge badgeContent={eventCount} color="primary">
            <Typography variant="body2">Events</Typography>
          </Badge>
        )}
        {lastRefresh && (
          <Chip
            label={`Updated ${getTimeSinceRefresh()}`}
            size="small"
            variant="outlined"
            color={refreshing ? 'primary' : 'default'}
          />
        )}
        {actions}
        {onRefresh && (
          <IconButton onClick={onRefresh} size="small" disabled={refreshing}>
            <RefreshIcon
              sx={{
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default ObservatoryHeader;

